import { forwardRef } from "react";
import { Link, LinkProps, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

const LOCALE_RE = /^\/(en|pt|es)(\/.*)?$/i;

const getActiveLocale = (pathname: string, language: string): string => {
  const m = pathname.match(LOCALE_RE);
  if (m) return m[1].toLowerCase();
  return language.toLowerCase();
};

export const localizeHref = (to: string, locale: string): string => {
  if (!to || typeof to !== "string") return to;
  // Skip external/anchor/non-internal hrefs
  if (
    to.startsWith("http://") ||
    to.startsWith("https://") ||
    to.startsWith("mailto:") ||
    to.startsWith("tel:") ||
    to.startsWith("#")
  ) {
    return to;
  }

  // Normalize to start with "/"
  const path = to.startsWith("/") ? to : `/${to}`;

  // If already prefixed with a locale, return as-is
  if (LOCALE_RE.test(path)) return path;

  // English lives at the root — no /en prefix. This matches seo.ts and the sitemap.
  if (locale === "en") return path;

  // Root edge case → /{locale}/
  if (path === "/") return `/${locale}/`;

  return `/${locale}${path}`;
};

interface LocalLinkProps extends Omit<LinkProps, "to"> {
  to: string;
}

const LocalLink = forwardRef<HTMLAnchorElement, LocalLinkProps>(({ to, ...rest }, ref) => {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const locale = getActiveLocale(pathname, language);
  return <Link ref={ref} to={localizeHref(to, locale)} {...rest} />;
});

LocalLink.displayName = "LocalLink";

export default LocalLink;

/** Returns a function that localizes a path with the active locale prefix. */
export const useLocalePath = () => {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const locale = getActiveLocale(pathname, language);
  return (to: string) => localizeHref(to, locale);
};
